/* Insigne — Material-aware variant + media + text binding for premium-landing template
 * On Material option change:
 *  1. Hide Size buttons that don't have a valid variant for the selected Material.
 *  2. If currently selected Size is invalid for new Material, auto-pick the first valid one.
 *  3. Filter gallery images by SKU prefix (KNV-* visible only when Canvas selected; others when Aluminium).
 *  4. Swap material-specific phrases in product description / FAQ / trust badges.
 *
 * Activation: only runs on pages where #InsigneMaterialBinding marker exists. Defensive: silent
 * no-op if the expected DOM is absent.
 */
(function () {
  'use strict';

  if (!document.getElementById('InsigneMaterialBinding')) return;

  // Phrases on initial render (Aluminium) → replacement when Canvas is selected.
  // Order matters: longer/more specific FIRST. Case-sensitive.
  const SWAPS_TO_CANVAS = [
    // ── Long-form FAQ answers (full sentences) ──
    [
      'The artwork is mounted on a 4mm premium aluminum composite panel (ACM). This rigid structure is engineered to remain perfectly flat and stable.',
      "Your artwork is printed on premium 340gsm cotton-poly canvas and ships rolled in a protective shipping tube. The canvas itself is unmounted — we recommend taking it to your local framer to stretch it onto wooden stretcher bars (gallery-wrap), float-mount it, or build a custom frame that complements your home's style."
    ],
    [
      'It arrives ready to hang with a pre-installed hidden floating system, creating a clean, gallery-style appearance slightly offset from the wall.',
      "Your canvas arrives rolled in a protective shipping tube. Take it to your local framer to choose your preferred presentation — stretch it onto gallery-wrap bars, float-mount it, or pair it with a custom frame in a style that matches your home. Pick whatever framing speaks to your taste."
    ],

    // ── Description "Premium Medium" block (h3 + paragraph + bullets) ──
    [
      'Premium Medium — Aluminum Composite Panel',
      'Premium Medium — 340gsm Rolled Canvas'
    ],
    [
      'Mounted on a rigid, modern aluminum composite panel for a sleek gallery look and extreme structural stability. This industrial-grade material is the perfect foundation for our heavy-build haptic textures.',
      'Printed on premium 340gsm cotton-poly canvas and shipped rolled in a protective tube — perfect for gallery-wrap stretching or your favorite custom frame. This archival-weight weave is the ideal foundation for our heavy-build haptic textures, ready for the framing presentation that matches your home.'
    ],
    [
      'Frameless Architectural Edges:',
      'Versatile Framing — Your Choice:'
    ],
    [
      ' A contemporary, minimalist presentation that creates a "floating" effect on your wall.',
      " Choose a gallery-wrap stretch, float-mount, or a custom frame from your local framer — pick the presentation that best fits your home's aesthetic."
    ],
    [
      'A contemporary, minimalist presentation that creates a "floating" effect on your wall.',
      "Choose a gallery-wrap stretch, float-mount, or a custom frame from your local framer — pick the presentation that best fits your home's aesthetic."
    ],
    [
      'Rigid & Warp-Proof:',
      'Archival Canvas Quality:'
    ],
    [
      ' Impact-resistant and designed to maintain a perfectly flat profile, preserving the 3D relief for a lifetime.',
      ' Premium 340gsm cotton-poly canvas resists fading and fiber stretching, preserving the 3D relief texture for years of gallery-quality enjoyment.'
    ],
    [
      'Impact-resistant and designed to maintain a perfectly flat profile, preserving the 3D relief for a lifetime.',
      'Premium 340gsm cotton-poly canvas resists fading and fiber stretching, preserving the 3D relief texture for years of gallery-quality enjoyment.'
    ],

    // ── Specs row: "Material" line — full phrases including the 4mm thickness token ──
    ['0.16" (4mm) Premium Aluminum Composite Panel', '340gsm Premium Canvas (Rolled, Unstretched)'],
    ['0.16" (4 mm) Premium Aluminum Composite Panel', '340gsm Premium Canvas (Rolled, Unstretched)'],
    ['0.16" (4mm) premium aluminum composite panel', '340gsm premium canvas (rolled, unstretched)'],
    ['0.16" (4 mm) premium aluminum composite panel', '340gsm premium canvas (rolled, unstretched)'],
    ['0.16" (4mm) Aluminum Composite Panel', '340gsm Canvas (Rolled, Unstretched)'],
    ['0.16" (4mm) aluminum composite panel', '340gsm canvas (rolled, unstretched)'],

    // ── Installation / ready-to-hang phrasing ──
    ['Ready to Hang Hardware Included', 'Ships Rolled — Stretching Required'],
    ['Ready to Hang (Pre-installed hidden floating system)', 'Ships Rolled (requires stretching or framing)'],
    ['ready to hang (pre-installed hidden floating system)', 'ships rolled (requires stretching or framing)'],
    ['Pre-installed hidden floating system', 'ships rolled, unmounted'],
    ['pre-installed hidden floating system', 'ships rolled, unmounted'],
    ['Ready to Hang', 'Ships Rolled'],
    ['ready to hang', 'ships rolled'],

    // ── Material identity, full phrases first ──
    ['Premium Aluminum Composite Panel (ACM)', '340gsm Premium Canvas (Rolled, Unstretched)'],
    ['premium aluminum composite panel (ACM)', '340gsm premium canvas (rolled, unstretched)'],
    ['Premium Aluminum Composite Panel', '340gsm Premium Canvas (Rolled, Unstretched)'],
    ['premium aluminum composite panel', '340gsm premium canvas (rolled, unstretched)'],
    ['Premium Aluminum Composite', '340gsm Premium Canvas'],
    ['premium aluminum composite', '340gsm premium canvas'],
    ['Aluminum Composite Panel (ACM)', '340gsm Canvas (Rolled, Unstretched)'],
    ['aluminum composite panel (ACM)', '340gsm canvas (rolled, unstretched)'],
    ['Aluminum Composite Panel', '340gsm Canvas (Rolled, Unstretched)'],
    ['aluminum composite panel', '340gsm canvas (rolled, unstretched)'],
    ['Aluminum Composite', '340gsm Canvas'],
    ['aluminum composite', '340gsm canvas'],
    ['Aluminium Composite', '340gsm Canvas'],
    ['aluminium composite', '340gsm canvas'],

    // ── Standalone thickness / parens cleanup (canvas isn't 4mm thick) ──
    ['0.16" (4 mm) ', ''],
    ['0.16" (4mm) ', ''],
    ['(4 mm) ', ''],
    ['(4mm) ', ''],
    ['(0.16") ', ''],
    [' (ACM)', ''],
    ['(ACM)', ''],

    // ── Generic aluminium → canvas (final fallbacks) ──
    ['4mm premium aluminum', '340gsm premium canvas'],
    ['4 mm premium aluminum', '340gsm premium canvas'],
    ['4mm aluminum', '340gsm canvas'],
    ['4 mm aluminum', '340gsm canvas'],
    ['Premium Aluminum', '340gsm Canvas'],
    ['premium aluminum', '340gsm canvas'],
    ['Aluminium', 'Rolled Canvas'],
    ['aluminium', 'rolled canvas'],
    ['Aluminum', 'Canvas'],
    ['aluminum', 'canvas'],
  ];

  // DOM containers where we want to swap material-specific text.
  const SWAP_CONTAINER_SELECTORS = [
    '.p-desc',                       // premium description
    '#PremiumFAQ',                   // premium FAQ wrapper
    '.faq-wrap',
    '.icon-row',                     // trust badges row containing "Premium Aluminum Composite"
    '.icon-box',                     // each trust badge
    '.productView-description',
    '.product-description',
    '.product__description',
    '.product-specs',
    '.product-specifications',
    '[data-product-tab]',
  ];

  function captureOriginalText(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        const p = node.parentElement;
        if (!p) return NodeFilter.FILTER_REJECT;
        const tag = p.tagName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT') return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    const nodes = [];
    let n;
    while ((n = walker.nextNode())) {
      if (n._insigneOriginal == null) n._insigneOriginal = n.nodeValue;
      nodes.push(n);
    }
    return nodes;
  }

  function applySwaps(textNodes, useCanvas) {
    textNodes.forEach((n) => {
      if (!useCanvas) {
        if (n._insigneOriginal != null) n.nodeValue = n._insigneOriginal;
        return;
      }
      let s = n._insigneOriginal != null ? n._insigneOriginal : n.nodeValue;
      for (const [from, to] of SWAPS_TO_CANVAS) {
        if (s.indexOf(from) !== -1) s = s.split(from).join(to);
      }
      n.nodeValue = s;
    });
  }

  // ─── Variant picker detection (premium section first, fallback to standard) ───
  function getVariantPickerContext() {
    // Premium section: .var-area > .var-group[data-option-index]
    const varArea = document.querySelector('.var-area');
    if (varArea) {
      const groups = Array.from(varArea.querySelectorAll('.var-group'));
      const labelMap = {};
      groups.forEach((g) => {
        const idx = parseInt(g.getAttribute('data-option-index'), 10);
        // The .var-label is the previous sibling span
        let labelEl = g.previousElementSibling;
        if (!labelEl || !labelEl.classList.contains('var-label')) {
          // Try inside parent
          const parent = g.parentElement;
          if (parent) labelEl = parent.querySelector('.var-label');
        }
        const name = (labelEl && labelEl.textContent || '').trim().toLowerCase();
        labelMap[idx] = { group: g, name };
      });
      return { kind: 'premium', root: varArea, groups: labelMap };
    }
    // Standard fieldset variant-radios
    const fieldsets = Array.from(document.querySelectorAll('fieldset[data-product-attribute="set-rectangle"]'));
    if (fieldsets.length) {
      const labelMap = {};
      fieldsets.forEach((fs) => {
        const idx = parseInt(fs.getAttribute('data-option-index'), 10);
        const legend = fs.querySelector('legend');
        const name = (legend && legend.textContent || '').trim().toLowerCase().split(':')[0].trim();
        labelMap[idx] = { group: fs, name };
      });
      return { kind: 'standard', root: document, groups: labelMap };
    }
    return null;
  }

  function findOptionByName(ctx, optionName) {
    const target = optionName.toLowerCase();
    const keys = Object.keys(ctx.groups);
    for (const k of keys) {
      const entry = ctx.groups[k];
      if (entry.name.indexOf(target) === 0) return { idx: parseInt(k, 10), entry };
    }
    return null;
  }

  function getOptionInputs(group) {
    return group.querySelectorAll('input[type="radio"]');
  }

  function getCurrentMaterial(ctx) {
    const m = findOptionByName(ctx, 'material');
    if (!m) return null;
    const checked = m.entry.group.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : null;
  }

  function readVariants() {
    // Try global productData (premium section) first
    if (typeof window.productData !== 'undefined' && Array.isArray(window.productData)) return window.productData;
    // Try embedded JSON in variant-radios
    const scripts = document.querySelectorAll('variant-radios script[type="application/json"], variant-selects script[type="application/json"]');
    for (const sc of scripts) {
      try {
        const data = JSON.parse(sc.textContent);
        if (Array.isArray(data) && data.length && 'option1' in data[0]) return data;
      } catch (e) { /* ignore */ }
    }
    return null;
  }

  function buildMaterialSizeMap(ctx, variants) {
    const m = findOptionByName(ctx, 'material');
    const s = findOptionByName(ctx, 'size');
    if (!m || !s) return null;
    const map = {};
    variants.forEach((v) => {
      // Variants from Shopify have option1, option2, option3 (1-indexed)
      const matVal = v[`option${m.idx + 1}`];
      const sizeVal = v[`option${s.idx + 1}`];
      if (!matVal || !sizeVal) return;
      if (!map[matVal]) map[matVal] = new Set();
      map[matVal].add(sizeVal);
    });
    Object.keys(map).forEach((k) => (map[k] = Array.from(map[k])));
    return { map, materialIdx: m.idx, sizeIdx: s.idx };
  }

  function updateSizeButtons(ctx, materialValue, mapping) {
    const s = findOptionByName(ctx, 'size');
    if (!s) return;
    const validSizes = new Set(mapping.map[materialValue] || []);
    const inputs = getOptionInputs(s.entry.group);
    let firstValidInput = null;
    let currentSelectedValid = false;
    inputs.forEach((inp) => {
      const v = inp.value;
      const lbl = s.entry.group.querySelector(`label[for="${inp.id}"]`);
      const wrapper = inp.parentElement; // for premium, input + label are siblings inside .var-group
      const isValid = validSizes.has(v);
      if (isValid) {
        inp.disabled = false;
        if (lbl) {
          lbl.style.display = '';
          lbl.classList.remove('insigne-hidden');
        }
        // For premium: also un-hide the radio input if hidden
        inp.style.display = '';
        if (!firstValidInput) firstValidInput = inp;
        if (inp.checked) currentSelectedValid = true;
      } else {
        inp.disabled = true;
        if (lbl) {
          lbl.style.display = 'none';
          lbl.classList.add('insigne-hidden');
        }
        inp.style.display = 'none';
        if (inp.checked) inp.checked = false;
      }
    });
    if (!currentSelectedValid && firstValidInput) {
      firstValidInput.checked = true;
      // Trigger change so theme JS re-evaluates the active variant.
      firstValidInput.dispatchEvent(new Event('change', { bubbles: true }));
      // Also click for any click-listeners
      try { firstValidInput.dispatchEvent(new Event('click', { bubbles: true })); } catch (e) {}
    }
  }

  function updateMediaByMaterial(materialValue) {
    const isCanvas = /canvas/i.test(materialValue || '');
    const galleries = document.querySelectorAll('.desktop-gallery, .mobile-slider-wrapper, .mobile-gallery, .product-page-gallery, .productView-images, .product-images, .swiper-wrapper, [data-product-gallery], .pdp-cinematic-gallery, .productMedia-wrapper, .product-single__media');
    galleries.forEach((g) => {
      const items = g.querySelectorAll('.gallery-item, .mobile-slide, .swiper-slide, .product-single__media-item, .productMedia-item, .product-image, [data-media-id], [data-product-media], .item, .slide, li');
      const targets = items.length ? items : g.querySelectorAll('img');
      targets.forEach((slide) => {
        const img = slide.tagName === 'IMG' ? slide : slide.querySelector('img');
        if (!img) {
          // Maybe a video wrapper without img — leave as-is
          return;
        }
        const src = img.getAttribute('src') || img.getAttribute('data-src') || '';
        const filename = (src.split('/').pop() || '').toLowerCase();
        const isKnv = /knv-/i.test(filename);
        const isEbm = /ebm-/i.test(filename);
        let show = true;
        if (isCanvas) {
          // Canvas → show only KNV. Everything else (EBM-only or unprefixed) is hidden.
          if (!isKnv) show = false;
        } else {
          // Aluminium → hide KNV. Show everything else.
          if (isKnv) show = false;
        }
        slide.style.display = show ? '' : 'none';
      });
    });
    // Refresh Swiper if present
    document.querySelectorAll('.swiper').forEach((el) => {
      if (el.swiper && typeof el.swiper.update === 'function') {
        try { el.swiper.update(); } catch (e) {}
      }
    });
  }

  function init() {
    const ctx = getVariantPickerContext();
    if (!ctx) return;
    const variants = readVariants();
    if (!variants || !variants.length) return;
    const mapping = buildMaterialSizeMap(ctx, variants);
    if (!mapping) return;

    // Capture original text in swap containers (once)
    const swapRoots = [];
    SWAP_CONTAINER_SELECTORS.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => swapRoots.push(el));
    });
    const allTextNodes = [];
    swapRoots.forEach((r) => allTextNodes.push.apply(allTextNodes, captureOriginalText(r)));

    function applyForMaterial(material) {
      if (!material) return;
      updateSizeButtons(ctx, material, mapping);
      updateMediaByMaterial(material);
      const isCanvas = /canvas/i.test(material);
      applySwaps(allTextNodes, isCanvas);
      toggleSampleSizeSection(isCanvas);
    }

    function toggleSampleSizeSection(useCanvas) {
      // Sample Size Information block lives inside .p-desc (product.description HTML).
      // It only applies to Aluminium — hide the entire block (and trailing <hr>) for Canvas.
      const desc = document.querySelector('.p-desc');
      if (!desc) return;
      let target = null;
      desc.querySelectorAll('h3').forEach((h) => {
        if (/sample size information/i.test(h.textContent || '')) target = h;
      });
      if (!target) return;
      const elementsToToggle = [target];
      let n = target.nextElementSibling;
      while (n) {
        if (n.tagName === 'H3') break;
        elementsToToggle.push(n);
        if (n.tagName === 'HR') break; // include the separator and stop
        n = n.nextElementSibling;
      }
      elementsToToggle.forEach((el) => {
        if (useCanvas) {
          if (el._insigneInlineDisplay == null) el._insigneInlineDisplay = el.style.display;
          el.style.display = 'none';
        } else {
          el.style.display = el._insigneInlineDisplay != null ? el._insigneInlineDisplay : '';
        }
      });
    }

    // Initial pass
    applyForMaterial(getCurrentMaterial(ctx));

    // Listen for Material radio changes (event delegation)
    const m = findOptionByName(ctx, 'material');
    if (m) {
      m.entry.group.addEventListener('change', (e) => {
        const t = e.target;
        if (t && t.matches('input[type="radio"]')) {
          applyForMaterial(t.value);
        }
      });
      // Some themes use click on label rather than change
      m.entry.group.addEventListener('click', (e) => {
        const lbl = e.target.closest && e.target.closest('label');
        if (!lbl) return;
        const forId = lbl.getAttribute('for');
        if (!forId) return;
        const input = document.getElementById(forId);
        if (input && input.matches('input[type="radio"]')) {
          // Defer until after the input is checked by the browser
          setTimeout(() => applyForMaterial(input.value), 0);
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 0);
  }
})();
